import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  HomeIcon,
  CreditCardIcon,
  DocumentTextIcon,
  UserCircleIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  CheckBadgeIcon,
  BuildingLibraryIcon
} from '@heroicons/react/24/solid';

interface GuideStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  tips: string[];
}

interface ApplicationGuideProps {
  isOpen: boolean;
  onClose: () => void;
}

const ApplicationGuide: React.FC<ApplicationGuideProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Reset to first step when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setShowModal(true);
      setTimeout(() => {
        setIsAnimating(true);
      }, 50);
    } else {
      setIsAnimating(false);
      setTimeout(() => {
        setShowModal(false);
      }, 300);
    }
  }, [isOpen]);

  // All guide steps - Updated with Express Pay
  const guideSteps: GuideStep[] = [
    {
      id: 'welcome',
      title: 'Welcome & Authentication',
      description: 'Start by entering your Applicant ID to find your application. An OTP will be sent to your registered email for secure authentication.',
      icon: <HomeIcon className="h-8 w-8 text-[#923d41]" />,
      tips: [
        'Enter your Applicant ID to get started',
        'OTP is sent to your registered email address',
        'Use the optional email field if your primary email is unavailable'
      ]
    },
    {
      id: 'authentication',
      title: 'Verify Your Identity',
      description: 'Enter the One-Time Password (OTP) sent to your email to complete authentication and access your invoices.',
      icon: <EnvelopeIcon className="h-8 w-8 text-[#923d41]" />,
      tips: [
        'Check your email inbox for the OTP code',
        'OTP expires after a short time for security',
        'Request a new OTP if you didn\'t receive it'
      ]
    },
    {
      id: 'invoices',
      title: 'Manage Your Invoices',
      description: 'View all invoices linked to your application. Each invoice shows the amount due, payment deadline, and payment status.',
      icon: <DocumentTextIcon className="h-8 w-8 text-[#923d41]" />,
      tips: [
        'Click on any invoice to view detailed information',
        'Scroll right on smaller devices to find payment links',
        'Check invoice status'
      ]
    },
    {
      id: 'payment',
      title: 'Make a Payment',
      description: 'Pay your fees easily using Express Pay. Application fees require full payment, while other invoices support installment payments.',
      icon: <CreditCardIcon className="h-8 w-8 text-[#923d41]" />,
      tips: [
        'Application fees must be paid in full',
        'Other invoices allow flexible installment payments',
        'Click "Pay" to process with Express Pay or "Back" to return to invoices'
      ]
    },
    {
      id: 'completion',
      title: 'Complete Your Payment with Express Pay',
      description: 'Finalize your payment securely through Express Pay. Choose from multiple payment options including Cards and Mobile Money.',
      icon: <BuildingLibraryIcon className="h-8 w-8 text-[#923d41]" />,
      tips: [
        'Express Pay supports Cards (Visa, Mastercard) and Mobile Money',
        'Enter your payment details securely',
        'Wait for payment processing to complete',
        'Check your email for payment confirmation',
        'Log out when you\'re done for security'
      ]
    }
  ];

  const handleNext = () => {
    if (currentStep < guideSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (index: number) => {
    setCurrentStep(index);
  };

  const handleClose = () => {
    onClose();
  };

  if (!showModal) return null;

  const currentStepData = guideSteps[currentStep];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with fade animation */}
      <div 
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ease-in-out ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Guide Modal with slide-up animation */}
      <div 
        className={`relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-all duration-300 ease-out ${
          isAnimating 
            ? 'opacity-100 translate-y-0 scale-100' 
            : 'opacity-0 translate-y-8 scale-95'
        }`}
        style={{ 
          transform: isAnimating ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#923d41] text-white p-4 rounded-t-2xl flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <QuestionMarkCircleIcon className="h-6 w-6" />
            <h2 className="text-xl font-bold">Application Guide</h2>
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
              {currentStep + 1}/{guideSteps.length}
            </span>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white transition-colors p-1 rounded-full hover:bg-white/20"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between gap-2">
            {guideSteps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => handleStepClick(index)}
                className={`flex-1 flex flex-col items-center gap-1 transition-all ${
                  index === currentStep 
                    ? 'text-[#923d41]' 
                    : index < currentStep 
                    ? 'text-green-500' 
                    : 'text-gray-300'
                }`}
                title={step.title}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  index === currentStep 
                    ? 'bg-[#923d41] text-white ring-2 ring-[#923d41] ring-offset-2' 
                    : index < currentStep 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {index < currentStep ? <CheckCircleIcon className="h-5 w-5" /> : index + 1}
                </div>
                <span className="text-[10px] hidden sm:block text-center font-medium">{step.title}</span>
              </button>
            ))}
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full mt-3 overflow-hidden">
            <div 
              className="h-1.5 bg-[#923d41] rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((currentStep + 1) / guideSteps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 p-3 bg-[#923d41]/10 rounded-xl">
              {currentStepData.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {currentStepData.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {currentStepData.description}
              </p>
            </div>
          </div>

          {/* Tips Section */}
          {currentStepData.tips && currentStepData.tips.filter(tip => tip.trim() !== '').length > 0 && (
            <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                <span>💡</span> Quick Tips
              </h4>
              <ul className="space-y-2">
                {currentStepData.tips.map((tip, index) => (
                  tip.trim() !== '' && (
                    <li key={index} className="flex items-start gap-2 text-sm text-blue-700">
                      <span className="text-blue-500 font-bold mt-0.5">•</span>
                      <span>{tip}</span>
                    </li>
                  )
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 p-4 rounded-b-2xl flex items-center justify-between border-t border-gray-200">
          <button
            onClick={handlePrevious}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
              currentStep > 0
                ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            disabled={currentStep === 0}
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Previous
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm hover:bg-gray-100 rounded-lg"
            >
              Skip Guide
            </button>
            <button
              onClick={handleNext}
              className={`px-6 py-2 rounded-lg flex items-center gap-2 transition-all shadow-sm ${
                currentStep < guideSteps.length - 1
                  ? 'bg-[#923d41] hover:bg-[#7a2e32] text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              {currentStep < guideSteps.length - 1 ? (
                <>
                  Next
                  <ChevronRightIcon className="h-4 w-4" />
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4" />
                  Finish
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationGuide;